import Carousel from 'components/Carousel/Carousel';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwipeableViews from 'react-swipeable-views';
import { ProductType } from 'types/product.types';
import { fallbackBlurImage } from 'utils/blurhash';
import ImageUrlResizer from 'utils/image-url-resizer';

import { NewListerBadge } from '../../NewListerBadge/NewListerBadge';
import ProductImageCarouselDots from './ProductImageCarouselDots/ProductImageCarouselDots';

type ProductImageCarouselProps = {
  product: ProductType;
  responsiveCarousel?: boolean;
  isNewLister?: boolean;
};

const ProductImageCarousel = (props: ProductImageCarouselProps) => {
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const [swipableActions, setSwipableActions] = useState<any>();
  const { product, responsiveCarousel, isNewLister } = props;
  const { t } = useTranslation();

  const updateHeight = () => {
    if (swipableActions) {
      swipableActions.updateHeight();
      // This does not exist in safari, used when window is resized
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(swipableActions.updateHeight);
      }
    }
  };

  useEffect(() => {
    if (responsiveCarousel) {
      window.addEventListener('resize', updateHeight);
    }

    return () => {
      if (responsiveCarousel) {
        window.removeEventListener('resize', updateHeight);
      }
    };
  }, []);

  useEffect(() => {
    updateHeight();
  }, [swipableActions]);

  const responsiveCarouselContent = () => (
    <div className="product-image-carousel-wrapper">
      <SwipeableViews
        index={activeImageIndex}
        animateHeight={true}
        enableMouseEvents={true}
        onChangeIndex={setActiveImageIndex}
        className="product-image-carousel"
        action={setSwipableActions}
      >
        {product?.images?.map((img) => (
          <React.Fragment key={img?.id}>
            <img src={ImageUrlResizer.resize(img.original, 1200, false)} alt="" draggable="false" onLoad={updateHeight} />
            {product.status === 'not-visible' && (
              <span className="visibility-block">
                <span className="visibility-block__icon" />
                <span className="visibility-block__text">{t('components.page-product.product-details.hidden')}</span>
              </span>
            )}
          </React.Fragment>
        ))}
      </SwipeableViews>
      <ProductImageCarouselDots items={product?.images || []} imgIndex={activeImageIndex} onChangeIndex={setActiveImageIndex} />
      {isNewLister && <NewListerBadge className="bottom spacing-main-half" />}
    </div>
  );

  const cropCarouselContent = () => (
    <Carousel
      className="product-image-carousel"
      slides={product?.images?.map((img) => (
        <>
          <img src={img?.blurHash || fallbackBlurImage} alt="" draggable="false" className="carousel-blurhash" />
          <img src={img.gallery} alt="" draggable="false" />
          {product.status === 'not-visible' && (
            <span className="visibility-block">
              <span className="visibility-block__icon" />
              <span className="visibility-block__text">{t('components.page-product.product-details.hidden')}</span>
            </span>
          )}
        </>
      ))}
    />
  );

  return <>{responsiveCarousel ? responsiveCarouselContent() : cropCarouselContent()}</>;
};

export default ProductImageCarousel;
